import { z } from 'zod/v4';
/**
 * A Zod schema for JSON objects.
 * @example
 * JsonObjectSchema.pipe(LocalStorageDataSchema).parse({ /* ... * / });
 */
export const JsonObjectSchema = z.string().transform((str, ctx) => {
    try {
        return JSON.parse(str);
    }
    catch (_error) {
        ctx.issues.push({ code: 'custom', input: str, message: 'Invalid JSON' });
        return z.NEVER;
    }
});
/**
 * Creates a Zod schema that validates function types with proper type inference for Zod v4.
 *
 * This function solves the breaking change in Zod v4 where `z.function()` is no longer a Zod schema
 * and cannot be used in object schemas. It provides proper TypeScript type inference, ensuring that
 * `z.infer<>` correctly extracts function signatures instead of showing `unknown`.
 *
 * **Problem Solved**: In Zod v3, you could use `z.function()` directly in object schemas, but in v4
 * this causes type inference issues where function properties show as `unknown` instead of their
 * actual function types.
 *
 * **Solution**: Uses the community-approved workaround from GitHub issue #4143, leveraging
 * `z.custom<Parameters<T['implement']>[0]>` to preserve function type information for proper inference.
 *
 * @template TInput - Zod tuple schema for function input parameters
 * @template TOutput - Zod schema for function return type
 *
 * @param input - Zod tuple schema defining the function's parameter types
 * @param output - Zod schema defining the function's return type
 *
 * @returns A Zod schema that validates functions and preserves correct TypeScript type inference
 *
 * @example
 * ```typescript
 * // ✅ BEFORE (Zod v3 - no longer works in v4)
 * const oldSchema = z.object({
 *   calculate: z.function().args(z.number()).returns(z.number())
 * });
 * // ❌ In v4: parsed.calculate shows as 'unknown' type
 *
 * // ✅ AFTER (Zod v4 - proper type inference)
 * const newSchema = z.object({
 *   name: z.string(),
 *   calculate: createFunctionSchema(z.tuple([z.number()]), z.number()),
 *   calculateAsync: createFunctionSchema(z.tuple([z.number()]), z.promise(z.number())),
 *   processData: createFunctionSchema(z.tuple([z.string(), z.boolean()]), z.void()),
 *   getData: createFunctionSchema(z.tuple([]), z.string()) // No parameters
 * });
 *
 * const parsed = newSchema.parse({
 *   name: "test",
 *   calculate: (n: number) => n * 2,                       // ✅ Correctly typed
 *   calculateAsync: (n: number) => Promise.resolve(n * 4), // ✅ Correctly typed
 *   processData: (str: string, flag: boolean) => console.log(str, flag),
 *   getData: () => "hello"
 * });
 *
 * // ✅ TypeScript now correctly infers these types:
 * // parsed.calculate: (n: number) => number
 * // parsed.calculateAsync: (n: number) => Promise<number>
 * const result = parsed.calculate(5); // result: number
 * const asyncResult = await parsed.calculateAsync(10); // asyncResult: number
 * ```
 *
 * @example
 * ```typescript
 * // Complex function types
 * const apiSchema = z.object({
 *   // Function with object parameter
 *   processUser: createFunctionSchema(
 *     z.tuple([z.object({ id: z.string(), name: z.string() })]),
 *     z.promise(z.boolean())
 *   ),
 *
 *   // Function with multiple parameters
 *   updateRecord: createFunctionSchema(
 *     z.tuple([z.string(), z.number(), z.optional(z.boolean())]),
 *     z.void()
 *   )
 * });
 * ```
 *
 * @remarks
 * **Type Safety**: Runtime validation only checks that the value is a function. Parameter and return
 * types are enforced by TypeScript at compile-time, not validated at runtime for performance.
 *
 * **Migration from v3**: Replace `z.function().args(...).returns(...)` with
 * `createFunctionSchema(z.tuple([...]), ...)` to restore type inference in object schemas.
 *
 * **GitHub Issue**: This implementation directly addresses the type inference problems discussed
 * in Zod GitHub issue #4143, using the community-approved solution.
 *
 * **Performance**: Minimal runtime overhead - only validates `typeof value === 'function'` without
 * calling the actual function implementation or parameter validation.
 *
 * @see https://zod.dev/v4/changelog - Zod v4 breaking changes
 * @see https://zod.dev/api#functions - Zod function documentation
 * @see https://github.com/colinhacks/zod/issues/4143 - GitHub issue discussion and solution
 * @since Zod v4
 */
export function createFunctionSchema(input, output) {
    // Implementation based on GitHub issue #4143 discussion
    const schema = z.function({ input, output });
    // Use the pattern from bstein-lattice's comment that handles type inference correctly
    const createFunctionSchema = (schema) => z.custom((fn) => {
        if (typeof fn !== 'function') {
            return false;
        }
        // We validate it's a function but don't call implement to avoid runtime overhead
        return true;
    }, { message: 'Expected a function' });
    return createFunctionSchema(schema);
}
//# sourceMappingURL=zod.js.map